<template>
  <div class="dropdown" ref="dropdown" @click.stop>
    <div class="dropdown-trigger" @click="handleToggle">
      <slot name="trigger">
        <span>下拉菜单</span>
      </slot>
    </div>
    <transition name="dropdown-fade">
      <div v-show="visible" class="dropdown-menu" :style="menuStyle">
        <slot>
          <div
            v-for="(item, index) in options"
            :key="index"
            class="dropdown-item"
            :class="{ 'is-disabled': item.disabled, 'is-divided': item.divided }"
            @click="handleItemClick(item)"
          >
            <span>{{ item.label }}</span>
          </div>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  props: {
    // 选项数据，格式: [{ label: '选项1', value: '1', disabled: false, divided: false }]
    options: {
      type: Array,
      default: () => [],
    },
    // 触发方式: 'click' | 'hover'
    trigger: {
      type: String,
      default: 'click',
      validator: (value) => ['click', 'hover'].includes(value),
    },
    // 菜单对齐方式: 'start' | 'center' | 'end'
    placement: {
      type: String,
      default: 'start',
      validator: (value) => ['start', 'center', 'end'].includes(value),
    },
  },
  data() {
    return {
      visible: false,
      menuStyle: {},
    };
  },
  mounted() {
    // 点击外部关闭
    document.addEventListener('click', this.handleClickOutside);
    // 窗口大小改变时重新计算位置
    window.addEventListener('resize', this.updateMenuPosition);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.updateMenuPosition);
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateMenuPosition();
        });
      }
    },
  },
  methods: {
    handleToggle() {
      if (this.trigger === 'click') {
        this.visible = !this.visible;
      }
    },
    handleItemClick(item) {
      if (item.disabled) {
        return;
      }
      this.$emit('command', item.value || item.label, item);
      this.visible = false;
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.visible = false;
      }
    },
    updateMenuPosition() {
      if (!this.$refs.dropdown) {
        return;
      }
      const trigger = this.$refs.dropdown.querySelector('.dropdown-trigger');
      const menu = this.$refs.dropdown.querySelector('.dropdown-menu');
      if (!trigger || !menu) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = 0;
      const top = triggerRect.height + 4; // 4px 间距

      // 根据 placement 计算水平位置
      if (this.placement === 'start') {
        left = 0;
      } else if (this.placement === 'center') {
        left = (triggerRect.width - menuRect.width) / 2;
      } else if (this.placement === 'end') {
        left = triggerRect.width - menuRect.width;
      }

      // 防止菜单超出视口右侧
      const menuRight = triggerRect.left + left + menuRect.width;
      if (menuRight > viewportWidth) {
        left = viewportWidth - triggerRect.left - menuRect.width - 10;
      }

      // 防止菜单超出视口左侧
      if (triggerRect.left + left < 0) {
        left = -triggerRect.left + 10;
      }

      // 如果下方空间不足，显示在上方
      let actualTop = top;
      const menuBottom = triggerRect.bottom + menuRect.height;
      if (menuBottom > viewportHeight && triggerRect.top > menuRect.height) {
        actualTop = -menuRect.height - 4;
      }

      this.menuStyle = {
        left: `${left}px`,
        top: `${actualTop}px`,
      };
    },
    show() {
      this.visible = true;
    },
    hide() {
      this.visible = false;
    },
  },
};
</script>

<style lang="less" scoped>
.dropdown {
  position: relative;
  display: inline-block;

  .dropdown-trigger {
    cursor: pointer;
    user-select: none;
  }

  .dropdown-menu {
    position: absolute;
    min-width: 120px;
    background: #fff;
    border: 1px solid rgba(113, 73, 255, 0.3);
    border-radius: 6px;
    box-shadow: 0 4px 12px 0 rgba(113, 73, 255, 0.15);
    padding: 4px 0;
    z-index: 1000;
    margin: 0;
    list-style: none;

    .dropdown-item {
      padding: 0 16px;
      line-height: 36px;
      font-size: 14px;
      color: #333;
      cursor: pointer;
      white-space: nowrap;
      user-select: none;
      transition: all 0.2s;

      &:hover:not(.is-disabled) {
        background: linear-gradient(90deg, rgba(113, 73, 255, 0.1) 0%, rgba(205, 94, 251, 0.1) 100%);
        color: #7149FF;
      }

      &.is-disabled {
        color: #c0c4cc;
        cursor: not-allowed;
        opacity: 0.6;
      }

      &.is-divided {
        border-top: 1px solid rgba(113, 73, 255, 0.2);
        margin-top: 4px;
        padding-top: 4px;
      }
    }
  }
}

// 淡入淡出动画
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
  transform-origin: top center;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: scaleY(0.8);
}
</style>
